"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { generatePresignedUrl } from "../postFileAws"
import { getStudentInfo } from "./api"
import {
  ArrowLeft,
  BookOpen,
  Play,
  FileText,
  FileQuestion,
  CheckCircle,
  Users,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Download,
  Eye,
  Clock,
  Award,
  Target,
  RotateCcw,
  Star,
  Menu,
  X,
} from "lucide-react"
import { postAws } from "../aws"

const extractYoutubeVideoId = (url) => {
  if (!url) return null
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}

const getContentIcon = (type) => {
  switch (type) {
    case "video":
      return <Play className="w-4 h-4 text-red-500" />
    case "pdf":
      return <FileText className="w-4 h-4 text-blue-500" />
    case "quiz":
      return <FileQuestion className="w-4 h-4 text-purple-500" />
    default:
      return <BookOpen className="w-4 h-4 text-gray-500" />
  }
}

// Course Rating Modal Component
const CourseRatingModal = ({ isOpen, onClose, onSubmit, courseName, isSubmitting }) => {
  const [rating, setRating] = useState(0)
  const [hoveredRating, setHoveredRating] = useState(0)

  const handleSubmit = () => {
    if (rating > 0) {
      onSubmit(rating)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6 mx-4">
        <div className="text-center mb-4 sm:mb-6">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Star className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Rate This Course</h2>
          <p className="text-sm sm:text-base text-gray-600">
            You've completed 50% of "{courseName}". How would you rate your experience so far?
          </p>
        </div>

        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="flex space-x-1 sm:space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="p-1 transition-transform hover:scale-110"
                disabled={isSubmitting}
              >
                <Star
                  className={`w-6 h-6 sm:w-8 sm:h-8 transition-colors ${
                    star <= (hoveredRating || rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm sm:text-base"
          >
            Skip for now
          </button>
          <button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting}
            className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {isSubmitting ? "Submitting..." : "Submit Rating"}
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-3 sm:mt-4">
          Your feedback helps us improve the course content
        </p>
      </div>
    </div>
  )
}

const QuizComponent = ({
  quizData,
  onComplete,
  courseId,
  topicId,
  subtopicId,
  onQuizSubmit,
  initialAnswers = [],
  showResults: initialShowResults = false,
  marksScored: initialMarksScored = null,
  quizKey,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [userAnswers, setUserAnswers] = useState(() => {
    if (initialShowResults && initialMarksScored !== null) {
      const answersObj = {}
      initialAnswers.forEach((answer) => {
        const questionIndex = Number.parseInt(answer.questionId.replace("q", "")) - 1
        if (!isNaN(questionIndex)) {
          answersObj[questionIndex] = Number.parseInt(answer.selectedOption)
        }
      })
      return answersObj
    }
    return {}
  })
  const [showResults, setShowResults] = useState(initialShowResults && initialMarksScored !== null)
  const [quizStarted, setQuizStarted] = useState(initialShowResults)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if ((initialShowResults || (initialAnswers && initialAnswers.length > 0)) && initialMarksScored !== null) {
      setShowResults(true)
      setQuizStarted(true)
      const answersObj = {}
      initialAnswers.forEach((answer) => {
        const questionIndex = Number.parseInt(answer.questionId.replace("q", "")) - 1
        if (!isNaN(questionIndex)) {
          answersObj[questionIndex] = Number.parseInt(answer.selectedOption)
        }
      })
      setUserAnswers(answersObj)
    } else {
      setShowResults(false)
      setQuizStarted(false)
      setCurrentQuestionIndex(0)
      setUserAnswers({})
      setIsSubmitting(false)
    }
  }, [quizKey, initialShowResults, initialMarksScored, initialAnswers])

  const questions = Array.isArray(quizData) ? quizData : []

  const handleAnswerSelect = (questionIndex, answer) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }))
  }

  const calculateResults = () => {
    if (initialMarksScored !== null && initialShowResults) {
      let correctAnswers = 0
      let totalMarks = 0

      questions.forEach((question) => {
        totalMarks += question.marks || 1
      })

      correctAnswers = Math.round(questions.length * (initialMarksScored / totalMarks))

      return {
        correctAnswers,
        totalQuestions: questions.length,
        obtainedMarks: initialMarksScored,
        totalMarks,
        percentage: totalMarks > 0 ? Math.round((initialMarksScored / totalMarks) * 100) : 0,
      }
    }

    let correctAnswers = 0
    let totalMarks = 0
    let obtainedMarks = 0

    questions.forEach((question, index) => {
      const userAnswer = userAnswers[index]
      const correctAnswer = question.correctAnswer
      const questionMarks = question.marks || 1

      totalMarks += questionMarks

      let isCorrect = false
      if (question.questionType === "multiple-choice") {
        const correctIndex = Number.parseInt(correctAnswer, 10)
        isCorrect = userAnswer === correctIndex
      } else if (question.questionType === "true-false") {
        isCorrect = userAnswer === correctAnswer
      } else if (question.questionType === "short-answer") {
        isCorrect = userAnswer?.toLowerCase().trim() === correctAnswer?.toLowerCase().trim()
      }

      if (isCorrect) {
        correctAnswers++
        obtainedMarks += questionMarks
      }
    })

    return {
      correctAnswers,
      totalQuestions: questions.length,
      obtainedMarks,
      totalMarks,
      percentage: totalMarks > 0 ? Math.round((obtainedMarks / totalMarks) * 100) : 0,
    }
  }

  const handleQuizSubmit = async () => {
    setIsSubmitting(true)
    try {
      const results = calculateResults()

      const answersArray = questions.map((question, index) => ({
        questionId: question.id || `q${index + 1}`,
        selectedOption: userAnswers[index] !== undefined ? userAnswers[index].toString() : "",
      }))

      await onQuizSubmit({
        courseId,
        topicId,
        subtopicId,
        subtopicType: "quiz",
        marksScored: results.obtainedMarks,
        totalMarks: results.totalMarks,
        answers: answersArray,
      })

      setShowResults(true)

      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (error) {
      console.error("Error submitting quiz:", error)
      setShowResults(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const results = showResults ? calculateResults() : null

  if (!quizStarted) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 sm:p-8 text-center border border-purple-200">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <FileQuestion className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Ready to Take the Quiz?</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            This quiz contains {questions.length} questions. Take your time and answer each question carefully.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span>No time limit</span>
            </div>
            <div className="flex items-center">
              <Target className="w-4 h-4 mr-2" />
              <span>{questions.length} questions</span>
            </div>
          </div>
          <button
            onClick={() => setQuizStarted(true)}
            className="bg-purple-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm sm:text-base"
          >
            Start Quiz
          </button>
        </div>
      </div>
    )
  }

  if (showResults) {
    return (
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between">
              <div className="text-center sm:text-left mb-4 sm:mb-0">
                <h2 className="text-xl sm:text-2xl font-bold mb-2">Quiz Results</h2>
                <p className="text-purple-100 text-sm sm:text-base">Here's how you performed</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold">{results.percentage}%</div>
                <div className="text-purple-100 text-sm">Score</div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-green-600">{results.correctAnswers}</div>
                <div className="text-gray-600 text-sm sm:text-base">Correct Answers</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                  {results.obtainedMarks}/{results.totalMarks}
                </div>
                <div className="text-gray-600 text-sm sm:text-base">Marks Obtained</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-purple-600">{results.totalQuestions}</div>
                <div className="text-gray-600 text-sm sm:text-base">Total Questions</div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Question Review</h3>
            <div className="space-y-4">
              {questions.map((question, index) => {
                const userAnswer = userAnswers[index]
                const correctAnswer = question.correctAnswer
                const questionMarks = question.marks || 1

                let isCorrect = false
                let userAnswerText = ""
                let correctAnswerText = ""

                if (question.questionType === "multiple-choice") {
                  const correctIndex = Number.parseInt(correctAnswer, 10)
                  isCorrect = userAnswer === correctIndex
                  userAnswerText = question.options[userAnswer] || "Not answered"
                  correctAnswerText = question.options[correctIndex] || ""
                } else if (question.questionType === "true-false") {
                  isCorrect = userAnswer === correctAnswer
                  userAnswerText = userAnswer || "Not answered"
                  correctAnswerText = correctAnswer
                } else if (question.questionType === "short-answer") {
                  isCorrect = userAnswer?.toLowerCase().trim() === correctAnswer?.toLowerCase().trim()
                  userAnswerText = userAnswer || "Not answered"
                  correctAnswerText = correctAnswer
                }

                return (
                  <div
                    key={index}
                    className={`p-3 sm:p-4 rounded-lg border-2 ${isCorrect ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base mb-2 sm:mb-0 flex-1">
                        Question {index + 1}: {question.question || question.text}
                      </h4>
                      <div className="flex items-center space-x-2">
                        {isCorrect ? (
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        ) : (
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-red-500 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full"></div>
                          </div>
                        )}
                        <span className="text-xs sm:text-sm font-medium text-gray-600">
                          {isCorrect ? `+${questionMarks}` : "0"} marks
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Your Answer: </span>
                        <span className={isCorrect ? "text-green-700" : "text-red-700"}>{userAnswerText}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Correct Answer: </span>
                        <span className="text-green-700">{correctAnswerText}</span>
                      </div>
                    </div>

                    {question.explanation && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="font-medium text-gray-700 text-xs sm:text-sm">Explanation: </span>
                        <span className="text-gray-600 text-xs sm:text-sm">{question.explanation}</span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="flex justify-center mt-6 sm:mt-8">
              <button
                onClick={() => {
                  setShowResults(false)
                  setQuizStarted(false)
                  setCurrentQuestionIndex(0)
                  setUserAnswers({})
                }}
                className="flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Retake Quiz
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const currentQuestion = questions[currentQuestionIndex]
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100

  return (
    <div className="max-w-3xl mx-auto px-4">
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 space-y-1 sm:space-y-0">
          <span className="text-xs sm:text-sm font-medium text-gray-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="text-xs sm:text-sm font-medium text-gray-600">{Math.round(progress)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start mb-4">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-0 flex-1">
            {currentQuestion.question || currentQuestion.text}
          </h3>
          {currentQuestion.marks && (
            <span className="bg-purple-100 text-purple-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
              {currentQuestion.marks} marks
            </span>
          )}
        </div>

        <div className="space-y-2 sm:space-y-3">
          {currentQuestion.questionType === "multiple-choice" &&
            currentQuestion.options.map((option, optionIndex) => (
              <button
                key={optionIndex}
                onClick={() => handleAnswerSelect(currentQuestionIndex, optionIndex)}
                className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 transition-colors ${
                  userAnswers[currentQuestionIndex] === optionIndex
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-purple-300 hover:bg-purple-25"
                }`}
              >
                <div className="flex items-center">
                  <div
                    className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 ${
                      userAnswers[currentQuestionIndex] === optionIndex
                        ? "border-purple-500 bg-purple-500"
                        : "border-gray-300"
                    }`}
                  >
                    {userAnswers[currentQuestionIndex] === optionIndex && (
                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                    )}
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base">{option}</span>
                </div>
              </button>
            ))}

          {currentQuestion.questionType === "true-false" &&
            ["True", "False"].map((option) => (
              <button
                key={option}
                onClick={() => handleAnswerSelect(currentQuestionIndex, option)}
                className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 transition-colors ${
                  userAnswers[currentQuestionIndex] === option
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-purple-300"
                }`}
              >
                <div className="flex items-center">
                  <div
                    className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 ${
                      userAnswers[currentQuestionIndex] === option
                        ? "border-purple-500 bg-purple-500"
                        : "border-gray-300"
                    }`}
                  >
                    {userAnswers[currentQuestionIndex] === option && (
                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                    )}
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base">{option}</span>
                </div>
              </button>
            ))}

          {currentQuestion.questionType === "short-answer" && (
            <textarea
              value={userAnswers[currentQuestionIndex] || ""}
              onChange={(e) => handleAnswerSelect(currentQuestionIndex, e.target.value)}
              placeholder="Type your answer here..."
              className="w-full p-3 sm:p-4 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none resize-none text-sm sm:text-base"
              rows={4}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between space-y-2 sm:space-y-0">
        <button
          onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
          disabled={currentQuestionIndex === 0}
          className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          Previous
        </button>

        {currentQuestionIndex === questions.length - 1 ? (
          <button
            onClick={handleQuizSubmit}
            disabled={isSubmitting}
            className="px-6 sm:px-8 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {isSubmitting ? "Submitting..." : "Submit & Evaluate"}
          </button>
        ) : (
          <button
            onClick={() => setCurrentQuestionIndex(Math.min(questions.length - 1, currentQuestionIndex + 1))}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
          >
            Next
          </button>
        )}
      </div>
    </div>
  )
}

const ContentRenderer = ({ content, isActive, courseId, topicId, onContentViewed }) => {
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [showQuiz, setShowQuiz] = useState(false)
  const [videoWatched, setVideoWatched] = useState(false)
  const [pdfViewed, setPdfViewed] = useState(false)
  const videoTimeRef = useRef(0)
  const videoTimerRef = useRef(null)

  useEffect(() => {
    if (content.type === "video") {
      setVideoWatched(false)
      videoTimeRef.current = 0
      if (videoTimerRef.current) {
        clearTimeout(videoTimerRef.current)
        videoTimerRef.current = null
      }
    }
  }, [content.id, content.type])

  const handleVideoTimeUpdate = (e) => {
    if (videoWatched) return

    videoTimeRef.current = e.target.currentTime

    if (videoTimeRef.current > 0) {
      if (videoTimerRef.current) {
        clearTimeout(videoTimerRef.current)
      }

      videoTimerRef.current = setTimeout(() => {
        if (videoTimeRef.current >= 60 && !videoWatched) {
          setVideoWatched(true)
          onContentViewed({
            courseId,
            topicId,
            subtopicId: content.id,
            subtopicType: "video",
          })
        }
      }, 1000)
    }
  }

  useEffect(() => {
    if (isActive && content.type === "pdf") {
      setPdfViewed(false)
      const timer = setTimeout(() => {
        if (!pdfViewed) {
          setPdfViewed(true)
          onContentViewed({
            courseId,
            topicId,
            subtopicId: content.id,
            subtopicType: "pdf",
          })
        }
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [isActive, content.type, content.id, courseId, topicId, onContentViewed])

  useEffect(() => {
    return () => {
      if (videoTimerRef.current) {
        clearTimeout(videoTimerRef.current)
      }
    }
  }, [content.id])

  if (!isActive) return null

  const truncateDescription = (text, lines = 3) => {
    if (!text) return ""
    const words = text.split(" ")
    const wordsPerLine = 15
    const maxWords = lines * wordsPerLine

    if (words.length <= maxWords) return text
    return words.slice(0, maxWords).join(" ") + "..."
  }

  switch (content.type) {
    case "video":
      if (content.isYoutubeVideo && content.youtubeVideoId) {
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="w-full" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                <iframe
                  key={content.id}
                  src={`https://www.youtube.com/embed/${content.youtubeVideoId}?rel=0&modestbranding=1&enablejsapi=1`}
                  title={content.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                  className="rounded-t-xl"
                  onLoad={() => {
                    if (!videoWatched) {
                      if (videoTimerRef.current) {
                        clearTimeout(videoTimerRef.current)
                      }

                      videoTimerRef.current = setTimeout(() => {
                        if (!videoWatched) {
                          setVideoWatched(true)
                          onContentViewed({
                            courseId,
                            topicId,
                            subtopicId: content.id,
                            subtopicType: "video",
                          })
                        }
                      }, 10000)
                    }
                  }}
                />
              </div>
              {content.description && (
                <div className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">About this video</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {showFullDescription ? content.description : truncateDescription(content.description)}
                  </p>
                  {content.description.split(" ").length > 45 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="mt-2 text-blue-600 hover:text-blue-700 font-medium text-xs sm:text-sm"
                    >
                      {showFullDescription ? "Show less" : "Read more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      } else if (content.url) {
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <video
                key={content.id}
                src={content.url}
                controls
                className="w-full h-auto max-h-64 sm:max-h-96"
                controlsList="nodownload"
                onTimeUpdate={handleVideoTimeUpdate}
                onPlay={() => {
                  videoTimeRef.current = 0
                }}
                onLoadedData={() => {
                  setVideoWatched(false)
                  videoTimeRef.current = 0
                }}
              >
                Your browser does not support the video tag.
              </video>
              {content.description && (
                <div className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">About this video</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {showFullDescription ? content.description : truncateDescription(content.description)}
                  </p>
                  {content.description.split(" ").length > 45 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="mt-2 text-blue-600 hover:text-blue-700 font-medium text-xs sm:text-sm"
                    >
                      {showFullDescription ? "Show less" : "Read more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }
      return (
        <div className="flex items-center justify-center h-48 sm:h-64 bg-gray-100 rounded-lg">
          <div className="text-center">
            <Play className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm sm:text-base">Video content not available</p>
          </div>
        </div>
      )

    case "pdf":
      if (content.url) {
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-gray-50 p-3 sm:p-4 border-b">
                <div className="flex items-center mb-2 sm:mb-0">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mr-2" />
                  <span className="font-medium text-gray-700 text-sm sm:text-base">PDF Document</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => window.open(content.url, "_blank")}
                    className="flex items-center px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                  >
                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => {
                      const link = document.createElement("a")
                      link.href = content.url
                      link.download = content.title || "document.pdf"
                      link.click()
                    }}
                    className="flex items-center px-2 sm:px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-xs sm:text-sm"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Download
                  </button>
                </div>
              </div>
              <div className="h-[400px] sm:h-[600px]">
                <iframe src={content.url} className="w-full h-full" title={content.title}>
                  <p className="p-4 text-sm sm:text-base">
                    Your browser does not support embedded PDFs.{" "}
                    <a href={content.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      Click here to view the PDF
                    </a>
                  </p>
                </iframe>
              </div>
              {content.description && (
                <div className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">About this document</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {showFullDescription ? content.description : truncateDescription(content.description)}
                  </p>
                  {content.description.split(" ").length > 45 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="mt-2 text-blue-600 hover:text-blue-700 font-medium text-xs sm:text-sm"
                    >
                      {showFullDescription ? "Show less" : "Read more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }
      return (
        <div className="flex items-center justify-center h-48 sm:h-64 bg-gray-100 rounded-lg">
          <div className="text-center">
            <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm sm:text-base">PDF content not available</p>
          </div>
        </div>
      )

    case "quiz":
      let parsedQuestions = []
      try {
        if (typeof content.quizData === "string") {
          parsedQuestions = JSON.parse(content.quizData)
        } else if (Array.isArray(content.quizData)) {
          parsedQuestions = content.quizData
        }
      } catch (error) {
        console.error("Error parsing quiz data:", error)
        parsedQuestions = []
      }

      if (content.quizStatus === "generating") {
        return (
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 sm:p-8 rounded-lg text-center shadow-inner">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-blue-200 border-opacity-50"></div>
              <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FileQuestion className="h-6 h-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-blue-800 mb-2 sm:mb-3">Quiz is Being Prepared</h3>
            <p className="text-sm sm:text-base text-blue-700 mb-4 max-w-md mx-auto">
              We're creating a personalized quiz based on the content. This may take a minute or two.
            </p>
          </div>
        )
      }

      if (parsedQuestions.length > 0) {
        return (
          <div className="space-y-4 sm:space-y-6">
            {!showQuiz ? (
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <FileQuestion className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" />
                </div>

                {(content.completed || (content.answers && content.answers.length > 0)) &&
                content.marksScored !== null ? (
                  <>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Quiz Completed</h2>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                      <div className="flex items-center justify-center mb-2">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2" />
                        <span className="font-medium text-green-800 text-sm sm:text-base">
                          {content.marksScored !== null ? `You scored ${content.marksScored} marks` : "Quiz completed"}
                        </span>
                      </div>
                      <p className="text-green-700 text-xs sm:text-sm">
                        You've already completed this quiz. View your results below.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowQuiz(true)}
                      className="bg-blue-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base"
                    >
                      View Results
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                      Interactive Quiz Available
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-md mx-auto">
                      Test your understanding with {parsedQuestions.length} carefully crafted questions.
                    </p>
                    <button
                      onClick={() => setShowQuiz(true)}
                      className="bg-purple-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm sm:text-base"
                    >
                      Start Quiz
                    </button>
                  </>
                )}
              </div>
            ) : (
              <QuizComponent
                quizData={parsedQuestions}
                onComplete={() => setShowQuiz(false)}
                courseId={courseId}
                topicId={topicId}
                subtopicId={content.id}
                onQuizSubmit={onContentViewed}
                initialAnswers={content.answers || []}
                showResults={
                  (content.completed || (content.answers && content.answers.length > 0)) && content.marksScored !== null
                }
                marksScored={content.marksScored}
                quizKey={`${topicId}-${content.id}`}
              />
            )}
          </div>
        )
      }

      return (
        <div className="flex items-center justify-center h-48 sm:h-64 bg-gray-100 rounded-lg">
          <div className="text-center">
            <FileQuestion className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm sm:text-base">Quiz content not available</p>
          </div>
        </div>
      )

    default:
      return (
        <div className="flex items-center justify-center h-48 sm:h-64 bg-gray-100 rounded-lg">
          <div className="text-center">
            <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm sm:text-base">Content not available</p>
          </div>
        </div>
      )
  }
}

const ViewCoursesELearning = () => {
  const { courseId } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeContent, setActiveContent] = useState(null)
  const [expandedTopics, setExpandedTopics] = useState(new Set())
  const [completedContent, setCompletedContent] = useState(new Set())
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  // Rating modal state
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [isSubmittingRating, setIsSubmittingRating] = useState(false)
  const [hasShownRating, setHasShownRating] = useState(false)
  const [hasRatedCourse, setHasRatedCourse] = useState(false)

  const checkForRatingModal = useCallback(() => {
    if (!course || hasShownRating || hasRatedCourse) return

    if (course.raitings !== undefined) {
      console.log("Course already has a rating:", course.raitings)
      setHasRatedCourse(true)
      return
    }

    const totalContent = course.topics.reduce((total, topic) => total + topic.content.length, 0)
    const completedCount = completedContent.size
    const completionPercentage = totalContent > 0 ? (completedCount / totalContent) * 100 : 0

    if (completionPercentage >= 50) {
      setShowRatingModal(true)
      setHasShownRating(true)
    }
  }, [course, completedContent.size, hasShownRating, hasRatedCourse])

  const handleRatingSubmit = async (rating) => {
    setIsSubmittingRating(true)
    try {
      const studentId = localStorage.getItem("studentId")
      if (!studentId) {
        throw new Error("Student ID not found")
      }

      const requestBody = {
        request: "add-ratings-course",
        body: {
          studentId,
          courseId,
          rating,
        },
      }

      console.log("Submitting course rating:", requestBody)

      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", requestBody)

      if (response.statusCode === 200) {
        console.log("Course rating submitted successfully")
        setHasRatedCourse(true)
        setShowRatingModal(false)
      } else {
        console.error("Failed to submit course rating:", response)
        setShowRatingModal(false)
      }
    } catch (error) {
      console.error("Error submitting course rating:", error)
      setShowRatingModal(false)
    } finally {
      setIsSubmittingRating(false)
    }
  }

  const handleContentViewed = async (data) => {
    try {
      const studentId = localStorage.getItem("studentId")
      if (!studentId) {
        console.error("Student ID not found")
        return
      }

      const requestBody = {
        request: "student-viewed-subtopic",
        body: {
          studentId,
          ...data,
        },
      }

      console.log("Tracking content view:", requestBody)

      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", requestBody)

      if (response.statusCode === 200) {
        console.log("Content view tracked successfully for:", data.subtopicId)
        const contentKey = `${data.topicId}-${data.subtopicId}`
        setCompletedContent((prev) => {
          const newSet = new Set([...prev, contentKey])
          console.log("Updated completed content:", newSet)
          return newSet
        })

        if (data.subtopicType === "quiz") {
          console.log("Quiz completed, refreshing course data...")
          setTimeout(() => {
            fetchCourseDetails()
          }, 1000)
        }
      } else {
        console.error("Failed to track content view:", response)
      }
    } catch (error) {
      console.error("Error tracking content view:", error)
    }
  }

  const fetchCourseDetails = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const studentId = localStorage.getItem("studentId")
      if (!studentId) {
        throw new Error("Student ID not found")
      }

      console.log("Fetching student info for studentId:", studentId)
      const response = await getStudentInfo(studentId)

      let studentData
      if (typeof response === "string") {
        studentData = JSON.parse(response)
      } else {
        studentData = response
      }

      console.log("Student data received:", studentData)

      if (!studentData || !studentData.body || !studentData.body.data) {
        throw new Error("Invalid student data structure")
      }

      const sharedCourses = studentData.body.data.sharedCourses || []
      console.log("Shared courses:", sharedCourses)

      const courseData = sharedCourses.find((course) => course.courseId === courseId)

      if (!courseData) {
        throw new Error("Course not found in shared courses")
      }

      console.log("Found course data:", courseData)

      let thumbnailUrl = null
      if (courseData.thumbnailS3Uri && courseData.thumbnailS3Uri.startsWith("s3://")) {
        try {
          thumbnailUrl = await generatePresignedUrl(courseData.thumbnailS3Uri)
        } catch (error) {
          console.error("Error generating presigned URL for thumbnail:", error)
        }
      }

      const processedTopics = await Promise.all(
        (courseData.topics || []).map(async (topic) => {
          const processedSubtopics = await Promise.all(
            (topic.subtopics || []).map(async (subtopic) => {
              let contentUrl = subtopic.contentUrl || subtopic.videoUrl || subtopic.pdfUrl
              let contentType = subtopic.contentType || "video"
              const isYoutubeVideo =
                subtopic.isYoutubeVideo ||
                (contentUrl && (contentUrl.includes("youtube.com") || contentUrl.includes("youtu.be")))
              let youtubeVideoId = null

              if (!subtopic.contentType) {
                if (subtopic.pdfUrl) {
                  contentType = "pdf"
                  contentUrl = subtopic.pdfUrl
                } else if (subtopic.quizData) {
                  contentType = "quiz"
                }
              }

              if (isYoutubeVideo && contentUrl) {
                youtubeVideoId = extractYoutubeVideoId(contentUrl)
              } else if (contentUrl && contentUrl.startsWith("s3://")) {
                try {
                  contentUrl = await generatePresignedUrl(contentUrl)
                } catch (error) {
                  console.error("Error generating presigned URL:", error)
                }
              }

              let parsedQuizData = null
              if (subtopic.quizData) {
                try {
                  parsedQuizData =
                    typeof subtopic.quizData === "string" ? JSON.parse(subtopic.quizData) : subtopic.quizData
                } catch (error) {
                  console.error("Error parsing quiz data:", error)
                }
              }

              return {
                id: subtopic.id,
                title: subtopic.title,
                description: subtopic.description || "",
                type: contentType,
                url: contentUrl,
                s3Uri: subtopic.contentUrl || subtopic.videoUrl || subtopic.pdfUrl,
                isYoutubeVideo: isYoutubeVideo,
                youtubeVideoId: youtubeVideoId,
                quizData: parsedQuizData,
                totalMarks: subtopic.totalMarks || 0,
                quizStatus: subtopic.quizStatus,
                completed: subtopic.completed || false,
                answers: subtopic.answers || [],
                marksScored: subtopic.marksScored || null,
              }
            }),
          )

          return {
            id: topic.id,
            title: topic.title,
            content: processedSubtopics,
          }
        }),
      )

      const processedCourse = {
        id: courseData.courseId,
        title: courseData.title || "Untitled Course",
        description: courseData.description || "",
        thumbnail: thumbnailUrl,
        topics: processedTopics,
        raitings: courseData.raitings,
      }

      setCourse(processedCourse)

      const initialCompleted = new Set()
      processedTopics.forEach((topic) => {
        topic.content.forEach((content) => {
          if (content.completed) {
            initialCompleted.add(`${topic.id}-${content.id}`)
          }
        })
      })

      console.log("Initial completed content:", initialCompleted)
      setCompletedContent(initialCompleted)

      if (processedTopics.length > 0) {
        setExpandedTopics(new Set([processedTopics[0].id]))
        if (processedTopics[0].content.length > 0) {
          setActiveContent({
            topicId: processedTopics[0].id,
            contentId: processedTopics[0].content[0].id,
          })
        }
      }
    } catch (err) {
      console.error("Error fetching course details:", err)
      setError("Failed to load course details. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }, [courseId])

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails()
    }
  }, [courseId, fetchCourseDetails])

  useEffect(() => {
    checkForRatingModal()
  }, [checkForRatingModal])

  const toggleTopic = (topicId) => {
    const newExpanded = new Set(expandedTopics)
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId)
    } else {
      newExpanded.add(topicId)
    }
    setExpandedTopics(newExpanded)
  }

  const selectContent = (topicId, contentId) => {
    setActiveContent({ topicId, contentId })
    setIsSidebarOpen(false) // Close sidebar on mobile when content is selected
  }

  const getActiveContentData = () => {
    if (!activeContent || !course) return null
    const topic = course.topics.find((t) => t.id === activeContent.topicId)
    if (!topic) return null
    const content = topic.content.find((c) => c.id === activeContent.contentId)
    return content ? { topic, content } : null
  }

  const activeContentData = getActiveContentData()

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-b from-blue-50 to-white px-4">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
        <p className="text-base sm:text-lg text-gray-600 font-medium text-center">Loading course...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Error</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300 shadow-md text-sm sm:text-base"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Course Rating Modal */}
      <CourseRatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onSubmit={handleRatingSubmit}
        courseName={course?.title || "Course"}
        isSubmitting={isSubmittingRating}
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-full mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/eLearning")}
                className="flex items-center text-gray-700 hover:text-gray-900 mr-4 sm:mr-6 transition-colors group"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 group-hover:-translate-x-1 transition-transform" />
                <span className="ml-2 font-medium text-sm sm:text-base hidden sm:inline">Back to Courses</span>
                <span className="ml-2 font-medium text-sm sm:hidden">Back</span>
              </button>

              {/* Mobile menu button */}

            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-2 sm:px-3 py-1 rounded-full">
                <span className="hidden sm:inline">Progress: </span>
                {completedContent.size} /{" "}
                {course?.topics.reduce((total, topic) => total + topic.content.length, 0) || 0}
                <span className="hidden sm:inline"> completed</span>
              </div>
            </div>
          </div>
        </div>
                      <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden flex items-center text-gray-700 hover:text-gray-900 transition-colors ml-1 p-2"
              >
                <Menu className="h-5 w-5" />
                <span className="ml-2 font-medium text-sm">Course Menu</span>
              </button>
      </header>

      <div className="flex flex-grow relative">
        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`
          fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
          w-80 bg-white border-r border-gray-200 
          h-[calc(100vh-56px)] sm:h-[calc(100vh-64px)] 
          overflow-y-auto flex-shrink-0 shadow-sm lg:shadow-none
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        >
          {/* Mobile close button */}
          <div className="lg:hidden flex justify-end p-4 border-b border-gray-200">
            <button onClick={() => setIsSidebarOpen(false)} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Course Header */}
          <div className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center space-x-3 sm:space-x-4">
              {course?.thumbnail ? (
                <img
                  src={course.thumbnail || "/placeholder.svg"}
                  alt={course.title}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-cover shadow-md"
                />
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center shadow-md">
                  <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2">{course?.title}</h1>
                <div className="flex items-center mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span>Self-paced learning</span>
                </div>
              </div>
            </div>
          </div>

          {/* Course Content */}
          <div className="p-3 sm:p-4">
            {course?.topics.map((topic, topicIndex) => (
              <div key={topic.id} className="mb-3 sm:mb-4">
                <button
                  onClick={() => toggleTopic(topic.id)}
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-blue-50 hover:to-purple-50 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                      <span className="text-blue-600 font-semibold text-xs sm:text-sm">{topicIndex + 1}</span>
                    </div>
                    <span className="font-medium text-gray-900 text-left text-sm sm:text-base">{topic.title}</span>
                  </div>
                  {expandedTopics.has(topic.id) ? (
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 transition-transform" />
                  ) : (
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 transition-transform" />
                  )}
                </button>

                {expandedTopics.has(topic.id) && (
                  <div className="mt-2 sm:mt-3 ml-3 sm:ml-4 space-y-1 sm:space-y-2">
                    {topic.content.map((content, contentIndex) => {
                      const isActive = activeContent?.topicId === topic.id && activeContent?.contentId === content.id
                      const isCompleted = completedContent.has(`${topic.id}-${content.id}`)

                      return (
                        <button
                          key={content.id}
                          onClick={() => selectContent(topic.id, content.id)}
                          className={`w-full flex items-center justify-between p-2 sm:p-3 rounded-lg transition-all duration-200 text-left group ${
                            isActive
                              ? "bg-blue-100 border-2 border-blue-200 shadow-md"
                              : "hover:bg-gray-50 border-2 border-transparent hover:border-gray-200"
                          }`}
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <div
                              className={`p-1.5 sm:p-2 rounded-lg mr-2 sm:mr-3 flex-shrink-0 ${
                                isActive ? "bg-blue-200" : "bg-gray-100 group-hover:bg-gray-200"
                              }`}
                            >
                              {getContentIcon(content.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span
                                className={`text-xs sm:text-sm font-medium block truncate ${isActive ? "text-blue-900" : "text-gray-700"}`}
                              >
                                {content.title}
                              </span>
                              <span className="text-xs text-gray-500 capitalize">{content.type}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {isCompleted && (
                              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto bg-gray-50 lg:ml-0">
          {activeContentData ? (
            <div className="max-w-5xl mx-auto">
              {/* Content Header */}
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row items-start text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                  <span className="bg-gray-100 px-2 py-1 rounded-full mb-2 sm:mb-0">
                    {activeContentData.topic.title}
                  </span>
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mx-0 sm:mx-2 hidden sm:block" />
                  <div className="flex items-center bg-gray-100 px-2 py-1 rounded-full">
                    {getContentIcon(activeContentData.content.type)}
                    <span className="ml-1 capitalize">{activeContentData.content.type}</span>
                  </div>
                </div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">
                  {activeContentData.content.title}
                </h1>
              </div>

              {/* Content Body */}
              <ContentRenderer
                content={activeContentData.content}
                isActive={true}
                courseId={courseId}
                topicId={activeContentData.topic.id}
                onContentViewed={handleContentViewed}
              />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Welcome to {course?.title}</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Select a lesson from the sidebar to start your learning journey
                </p>
                <div className="text-xs sm:text-sm text-gray-500">
                  <p>This course contains {course?.topics.length} chapters with interactive content</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ViewCoursesELearning
